import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from './ui/table';
import {
  Sparkles,
  Download,
  Trash2,
  Eye,
  Lock,
  Loader2,
  Search,
  ChevronLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { services } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'elitebgrowth2025';

export const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
      fetchSubmissions();
    } else {
      setPasswordError('Invalid password');
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setSubmissions(prev => prev.filter(s => s.id !== id));
        setDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadPDF = async (submission) => {
    setDownloadingId(submission.id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions/${submission.id}/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `submission-${submission.fullName || 'unknown'}-${submission.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const getServiceNames = (serviceIds) => {
    if (!serviceIds || !Array.isArray(serviceIds)) return '-';
    if (serviceIds.length === 0) return '-';
    return serviceIds
      .map(id => services.find(s => s.id === id)?.name || id)
      .join(', ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSubmissions = submissions.filter(sub => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (sub.fullName && sub.fullName.toLowerCase().includes(searchLower)) ||
      (sub.companyName && sub.companyName.toLowerCase().includes(searchLower)) ||
      (sub.country && sub.country.toLowerCase().includes(searchLower))
    );
  });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-xl">Admin Access</CardTitle>
            <p className="text-sm text-slate-500 mt-2">Enter password to access submissions</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className={cn(passwordError && 'border-red-500')}
                />
                {passwordError && (
                  <p className="text-xs text-red-500 mt-1">{passwordError}</p>
                )}
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
                <Lock className="h-4 w-4 mr-2" />
                Access Admin Panel
              </Button>
              <a
                href="/"
                className="block text-center text-sm text-slate-500 hover:text-emerald-600 transition-colors"
              >
                <ChevronLeft className="h-4 w-4 inline mr-1" />
                Back to main site
              </a>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Detail View Component
  const DetailView = ({ submission, onClose }) => {
    if (!submission) return null;

    const renderField = (label, value) => {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        return null;
      }
      let displayValue = value;
      if (Array.isArray(value)) {
        displayValue = value.join(', ');
      }
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No';
      }
      return (
        <tr className="border-b border-slate-100">
          <td className="py-2 pr-4 text-xs text-slate-500 font-medium whitespace-nowrap align-top">{label}</td>
          <td className="py-2 text-sm text-slate-800">{displayValue}</td>
        </tr>
      );
    };

    const renderSection = (title, fields) => {
      const renderedFields = fields.filter(f => f !== null);
      if (renderedFields.length === 0) return null;
      
      return (
        <div className="mb-6">
          <h4 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
            {title}
          </h4>
          <table className="w-full">
            <tbody>{renderedFields}</tbody>
          </table>
        </div>
      );
    };

    return (
      <Dialog open={!!submission} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg">
              Submission Details: {submission.fullName || 'Unknown'}
            </DialogTitle>
            <DialogDescription>
              Submitted on {formatDate(submission.submittedAt)}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-2">
              {/* Basic Information */}
              {renderSection('Basic Information', [
                renderField('Full Name', submission.fullName),
                renderField('Company / Brand', submission.companyName),
                renderField('Country / Target Market', submission.country),
                renderField('Existing Website', submission.existingWebsite),
                renderField('Selected Services', getServiceNames(submission.selectedServices)),
                renderField('Selected Package', submission.selectedPackage),
              ])}

              {/* Website Development */}
              {submission.selectedServices?.includes('website') && renderSection('Website Development', [
                renderField('Has Existing Website', submission.hasWebsite),
                renderField('Existing Website URL', submission.existingWebsiteUrl),
                renderField('Website Type', submission.websiteType),
                renderField('Website Type (Other)', submission.websiteTypeOther),
                renderField('Number of Pages', submission.pageCount),
                renderField('Product Count (E-commerce)', submission.productCount),
                renderField('Content Ready', submission.contentReady),
                renderField('Want Content Creation', submission.wantContentCreation),
                renderField('CMS Preference', submission.cmsPreference),
                renderField('CMS (Other)', submission.cmsOther),
                renderField('Features Needed', submission.websiteFeaturesNeeded),
                renderField('Features (Other)', submission.websiteFeaturesOther),
                renderField('Has Branded Email', submission.hasBrandedEmail),
              ])}

              {/* Mobile App Development */}
              {submission.selectedServices?.includes('mobile-app') && renderSection('Mobile App Development', [
                renderField('App Type', submission.appType),
                renderField('Platform', submission.appPlatform),
                renderField('Has UI/UX Design', submission.hasUiDesign),
                renderField('Want UI/UX Design', submission.wantUiDesign),
                renderField('Core Features', submission.appCoreFeatures),
                renderField('Needs Authentication', submission.needsAuth),
                renderField('Auth Methods', submission.authMethods),
                renderField('Needs Payment', submission.needsPayment),
                renderField('Payment Type', submission.paymentType),
              ])}

              {/* Digital Marketing */}
              {submission.selectedServices?.includes('digital-marketing') && renderSection('Digital Marketing', [
                renderField('Primary Goal', submission.marketingGoal),
                renderField('Target Market', submission.targetMarket),
                renderField('Services Needed', submission.marketingServicesNeeded),
                renderField('Monthly Budget', submission.marketingBudget),
                renderField('SEO Website URL', submission.seoWebsiteUrl),
                renderField('SEO Currently Active', submission.seoCurrentlyActive),
                renderField('Has Ad Account', submission.hasAdAccount),
                renderField('Social Platforms', submission.socialPlatforms),
                renderField('Needs Social Content', submission.needsSocialContent),
              ])}

              {/* Logo & Branding */}
              {submission.selectedServices?.includes('logo') && renderSection('Logo & Branding', [
                renderField('Brand Type', submission.brandType),
                renderField('Existing Logo File', submission.existingLogo),
                renderField('Logo Type', submission.logoType),
                renderField('Brand Style', submission.brandStyle),
                renderField('Brand Style (Other)', submission.brandStyleOther),
                renderField('Logo References', submission.logoReferences),
              ])}

              {/* Animation / Video */}
              {submission.selectedServices?.includes('animation') && renderSection('Animation / Explainer Video', [
                renderField('Video Type', submission.videoType),
                renderField('Video Type (Other)', submission.videoTypeOther),
                renderField('Duration', submission.videoDuration),
                renderField('Has Script', submission.hasScript),
                renderField('Want Script Writing', submission.wantScriptWriting),
                renderField('Needs Voice Over', submission.needsVoiceOver),
                renderField('Accent Preference', submission.accentPreference),
                renderField('Voice Preference', submission.voicePreference),
              ])}

              {/* eLearning */}
              {submission.selectedServices?.includes('elearning') && renderSection('eLearning Development', [
                renderField('Course Type', submission.courseType),
                renderField('Module/Slide Count', submission.moduleCount),
                renderField('Content Ready', submission.courseContentReady),
                renderField('Want Content Structure', submission.wantContentStructure),
                renderField('Needs LMS', submission.needsLms),
                renderField('Existing LMS', submission.existingLms),
                renderField('Preferred LMS', submission.preferredLms),
              ])}

              {/* LinkedIn Services */}
              {submission.selectedServices?.includes('linkedin') && renderSection('LinkedIn Services', [
                renderField('Profile Type', submission.linkedinProfileType),
                renderField('Services Needed', submission.linkedinServicesNeeded),
                renderField('Posting Frequency', submission.postingFrequency),
                renderField('Target Audience', submission.targetAudience),
                renderField('Has Outreach Messages', submission.hasOutreachMessages),
                renderField('Want Outreach Creation', submission.wantOutreachCreation),
              ])}

              {/* Content Writing */}
              {submission.selectedServices?.includes('content-writing') && renderSection('Content Writing', [
                renderField('Content Types Needed', submission.contentTypesNeeded),
                renderField('Content Details', submission.contentDetails),
              ])}

              {/* Project Clarity */}
              {renderSection('Project Clarity & Alignment', [
                renderField('Decision Maker', submission.decisionMaker),
                renderField('Decision Maker Details', submission.decisionMakerDetails),
                renderField('Success & Non-negotiables', submission.successAndNonNegotiables),
                renderField('Post Launch Needs', submission.postLaunchNeeds),
                renderField('Additional Notes', submission.additionalNotes),
              ])}
            </div>
          </ScrollArea>
          
          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={() => handleDownloadPDF(submission)}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={downloadingId === submission.id}
            >
              {downloadingId === submission.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-800">Admin Panel</h1>
              <p className="text-[10px] text-slate-500">EliteBGrowth Submissions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="text-xs text-slate-600 hover:text-emerald-600 transition-colors flex items-center gap-1"
            >
              <ChevronLeft className="h-3 w-3" />
              Back to Site
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAuthenticated(false)}
              className="text-xs"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search and Stats */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, company..."
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              {filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchSubmissions}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Submissions Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-slate-500">No submissions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                      <TableHead className="font-semibold">Country</TableHead>
                      <TableHead className="font-semibold">Services</TableHead>
                      <TableHead className="font-semibold">Submitted</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubmissions.map((submission) => (
                      <TableRow key={submission.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {submission.fullName || '-'}
                        </TableCell>
                        <TableCell>{submission.companyName || '-'}</TableCell>
                        <TableCell>{submission.country || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {submission.selectedServices?.slice(0, 2).map(serviceId => (
                              <span
                                key={serviceId}
                                className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded"
                              >
                                {services.find(s => s.id === serviceId)?.name || serviceId}
                              </span>
                            ))}
                            {submission.selectedServices?.length > 2 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                                +{submission.selectedServices.length - 2}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(submission.submittedAt)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => setSelectedSubmission(submission)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4 text-slate-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDownloadPDF(submission)}
                              disabled={downloadingId === submission.id}
                              title="Download PDF"
                            >
                              {downloadingId === submission.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4 text-slate-600" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteConfirm(submission)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail View Dialog */}
      <DetailView
        submission={selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Submission</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the submission from <strong>{deleteConfirm?.fullName}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm?.id)}
              disabled={deletingId === deleteConfirm?.id}
            >
              {deletingId === deleteConfirm?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
